/**
 * Header component — Alpine.js data for nav toggle + search overlay.
 */

import Alpine from 'alpinejs';

( () => {

	document.addEventListener( 'alpine:init', () => {

		Alpine.data( 'header', () => ({

			navOpen   : false,
			searchOpen: false,

			init() {

				// Whenever the mobile drawer's open state changes, reset it to a
				// fully-collapsed baseline. This covers the drawer opening via the
				// hamburger, closing again, and the rare case where it is already
				// open at load — so the mega never shows its desktop default-active
				// pane inside the drawer. $watch fires on change; the immediate call
				// handles a drawer that starts open.
				this.$watch( 'navOpen', () => this.collapseDrawer() );
				this.collapseDrawer();

				// Delegate top-level trigger clicks. Triggers (items with a
				// dropdown) render href="#" and must not navigate; on desktop the
				// dropdown opens on hover, on mobile the click toggles the
				// accordion. Leaf links (no dropdown) navigate normally.
				this.$el.addEventListener( 'click', ( e ) => {

					const trigger = e.target.closest( '.nav-trigger' );
					if ( ! trigger || ! this.$el.contains( trigger ) ) {
						return;
					}

					const item     = trigger.closest( '.nav-item' );
					const dropdown  = item?.querySelector( ':scope > .nav-dropdown' );
					if ( ! dropdown ) {
						return; // Leaf item — let the link navigate.
					}

					e.preventDefault(); // Trigger only — keep "#" out of the URL bar.

					// Accordion behaviour only while the mobile drawer is open.
					if ( ! this.navOpen ) {
						return;
					}

					const wasOpen = item.classList.contains( 'is-open' );
					item.parentElement
						.querySelectorAll( '.nav-item.is-open' )
						.forEach( ( el ) => el.classList.remove( 'is-open' ) );

					if ( ! wasOpen ) {
						item.classList.add( 'is-open' );
					}

					trigger.setAttribute( 'aria-expanded', ( ! wasOpen ).toString() );
				});
			},

			openSearch() {
				this.searchOpen = true;
				setTimeout( () => this.$refs.searchInput?.focus(), 310 );
			},

			closeSearch() {
				this.searchOpen = false;
			},

			toggleNav() {
				this.navOpen = ! this.navOpen;
			},

			// Reset the drawer to a fully-collapsed baseline: close any open
			// top-level accordion, and — only while the drawer is open — clear the
			// mega's default-active credential pill/pane so it starts collapsed on
			// mobile. The mega active state is left intact when the drawer is
			// closed, because the desktop mega relies on it to show a default pane.
			// Idempotent, so it is safe to run on every navOpen change and at init.
			collapseDrawer() {
				this.$el
					.querySelectorAll( '.nav-item.is-open' )
					.forEach( ( el ) => {
						el.classList.remove( 'is-open' );
						el.querySelector( ':scope > .nav-trigger' )?.setAttribute( 'aria-expanded', 'false' );
					});

				if ( ! this.navOpen ) {
					return;
				}

				this.$el
					.querySelectorAll( '.nav-mega-l2.active, .nav-mega-pane.active' )
					.forEach( ( el ) => el.classList.remove( 'active' ) );
			},
		}) );

		Alpine.data( 'navMega', () => ({

			init() {

				const tabs   = this.$el.querySelectorAll( '.nav-mega-l2[data-pane]' );
				const panes  = this.$el.querySelectorAll( '.nav-mega-pane[data-pane]' );
				const header = this.$el.closest( '.header-bar' );

				const isDrawerOpen = () => !! header?.classList.contains( 'nav-open' );

				const activate = ( key ) => {
					tabs.forEach( ( tab ) => tab.classList.toggle( 'active', null !== key && tab.dataset.pane === key ) );
					panes.forEach( ( pane ) => pane.classList.toggle( 'active', null !== key && pane.dataset.pane === key ) );
				};

				tabs.forEach( ( tab ) => {

					// Desktop: hover switches the single visible pane. Skipped while
					// the mobile drawer is open, where the pills act as accordions.
					tab.addEventListener( 'mouseenter', () => {
						if ( isDrawerOpen() ) {
							return;
						}
						activate( tab.dataset.pane );
					});

					tab.addEventListener( 'click', ( e ) => {
						e.preventDefault();

						// Mobile drawer: tapping the already-open pill collapses it.
						if ( isDrawerOpen() && tab.classList.contains( 'active' ) ) {
							activate( null );
							return;
						}

						activate( tab.dataset.pane );
					});
				});
			},
		}) );
	});

})();
